import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, LogIn } from 'lucide-react';
import { BrandMark } from '../components/layout/Brand';
import { LanguageSwitcher, languageChosenWhileSignedOut } from '../components/layout/LanguageSwitcher';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { VersionWithWhatsNew } from '../components/WhatsNew';
import { authApi, errorMessage } from '../services/api';
import { isLanguage, t, useLanguageStore } from '../i18n';
import { useAuthStore } from '../store';

interface LoginValues {
  username: string;
  password: string;
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuthStore();
  const [error, setError] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>();

  const from = (location.state as { from?: string } | null)?.from || '/';
  if (user) return <Navigate to={from} replace />;

  const onSubmit = async ({ username, password }: LoginValues) => {
    setError('');
    try {
      const response = await authApi.login(username, password);
      // A language picked here is saved with the account, otherwise the account's language is used
      const { language, setLanguage } = useLanguageStore.getState();
      if (languageChosenWhileSignedOut() && response.user.language !== language) {
        setUser(await authApi.setLanguage(language).catch(() => response.user));
      } else {
        setUser(response.user);
        if (isLanguage(response.user.language)) setLanguage(response.user.language);
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err, t('Prihlásenie sa nepodarilo')));
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-secondary-50 to-secondary-100 p-4">
      <LanguageSwitcher className="absolute right-4 top-4" />
      <VersionWithWhatsNew className="absolute bottom-4 right-4" />
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <BrandMark large />
        </div>
        <Card>
          <h1 className="mb-1 text-lg font-semibold text-secondary-900">{t('Prihlásenie')}</h1>
          <p className="mb-5 text-sm text-secondary-500">{t('Interný systém evidencie vozového parku')}</p>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
            <Input
              label={t('Používateľské meno')}
              autoComplete="username"
              autoFocus
              error={errors.username?.message}
              {...register('username', { required: t('Zadajte používateľské meno') })}
            />
            <Input
              label={t('Heslo')}
              type="password"
              autoComplete="current-password"
              error={errors.password?.message}
              {...register('password', { required: t('Zadajte heslo') })}
            />
            <Button type="submit" className="w-full" size="lg" loading={isSubmitting}>
              <LogIn className="h-4 w-4" />
              {t('Prihlásiť sa')}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
